import {
  createContext,
  useContext,
  useState,
  ReactElement,
  createElement,
  FunctionComponent,
} from 'react';
import { Modal } from '@capitalise/ui-design-system';
import { use100vh } from 'react-div-100vh';

export const ModalContext = createContext(null);

interface ModalContext {
  label: string;
  defaultValue: boolean;
  content: FunctionComponent<any>;
  defaultMetaData?: any;
}

interface ModalProviderProps {
  children?: ReactElement;
  contexts?: ModalContext[];
  defaultContext: ModalContext;
}

export const useModalContext = () => {
  const context = useContext(ModalContext);
  if (context === undefined) {
    throw new Error('useModalContext must be used within an ModalProvider');
  }
  return context ?? {};
};

export const ModalProvider = ({
  children,
  contexts,
  defaultContext,
}: ModalProviderProps): ReactElement => {
  const [currentContext, setCurrentContext] = useState(defaultContext);

  const [isOpen, setOpen] = useState(currentContext?.defaultValue);
  const [metaData, setMetaData] = useState(currentContext?.defaultMetaData);

  const close = () => {
    setOpen(false);
  };

  const open = (options, contextLabel: string) => {
    if (contextLabel) {
      const newContext = contexts.find(({ label }) => label === contextLabel);
      setCurrentContext(newContext);
    }

    setOpen(true);
    setMetaData(options);
  };

  const context = {
    close,
    open,
    isOpen,
    label: currentContext.label,
    metaData,
    setMetaData,
  };

  const height = use100vh();

  return (
    <ModalContext.Provider value={context}>
      {children}
      {isOpen ? (
        <Modal label={currentContext.label} height={height}>
          {createElement(currentContext.content, { ...currentContext.defaultMetaData })}
        </Modal>
      ) : null}
    </ModalContext.Provider>
  );
};
